




import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Dashboard from './Dashboard';
import JobSeekers from './JobSeeker';
import JobAssignments from './JobSeekerAssignment';
import SearchHistory from './SearchHistory';
import Jobs from './Jobs';
import Attendance from './Attendance';
import CertifyJobSeekers from '../Admin/CertifyJobSeekers';

const JuniorAdminDashboard = () => {
  const [view, setView] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
   const [activities, setActivities] = useState('');
  const [dashboardData, setDashboardData] = useState({ jobSeekers: [], stats: {} });
  const [searchHistory, setSearchHistory] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setError('No authentication token found. Please log in.');
        navigate('/jadminlogin');
        return;
      }

      try {
        if (user?.userType === 'superadmin') {
          const response = await fetch(`${process.env.REACT_APP_API_URL}admin/superadmin/all-activities`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await response.json();
          if (response.ok) {
            setActivities(data);
          } else {
            setError(data.message || 'Failed to fetch superadmin activities');
          }
        } else {
          const dashboardResponse = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/dashboard`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const dashboardData = await dashboardResponse.json();
          console.log(dashboardData)
          if (dashboardResponse.ok) {
            setUser(dashboardData.admin);
            setDashboardData({
              jobSeekers: dashboardData.jobSeekers || [],
              stats: dashboardData.stats || {}
            });
          } else {
            setError(dashboardData.message || 'Failed to fetch dashboard data');
          }

          await fetchSearchHistory(); // Initial fetch of search history
        }
      } catch (error) {
        setError('Server error while fetching data');
        console.error('Fetch error:', error);
      }
    };

    fetchData();
  }, [token, user?.userType, navigate]);

  const fetchSearchHistory = async () => {
    try {
      const historyResponse = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/search-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const historyData = await historyResponse.json();
      if (historyResponse.ok) {
        setSearchHistory(historyData.searchHistory || []);
      } else {
        setError(historyData.message || 'Failed to fetch search history');
      }
    } catch (error) {
      setError('Server error while fetching search history');
      console.error('Search history error:', error);
    }
  };

  const handleCreateJobSeeker = async (formData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/create-jobseeker`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok) {
        setDashboardData(prev => ({
          ...prev,
          jobSeekers: [...prev.jobSeekers, data.jobSeeker],
          stats: { ...prev.stats, totalJobSeekers: (prev.stats.totalJobSeekers || 0) + 1 }
        }));
        setError('');
        return { message: data.message };
      } else {
        setError(data.message);
        throw new Error(data.message);
      }
    } catch (error) {
      setError('Server error while creating jobseeker');
      console.error('Create jobseeker error:', error);
      throw error;
    }
  };

  const handleUpdateJobSeeker = async (id, formData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/update-jobseeker/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok) {
        setDashboardData(prev => ({
          ...prev,
          jobSeekers: prev.jobSeekers.map(js => (js._id === id ? { ...js, ...formData } : js))
        }));
        setError('');
        return { message: data.message };
      } else {
        setError(data.message);
        throw new Error(data.message);
      }
    } catch (error) {
      setError('Server error while updating jobseeker');
      console.error('Update jobseeker error:', error);
      throw error;
    }
  };

  const handleDeleteJobSeeker = async (id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/delete-jobseeker/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setDashboardData(prev => ({
          ...prev,
          jobSeekers: prev.jobSeekers.filter(js => js._id !== id),
          stats: { ...prev.stats, totalJobSeekers: (prev.stats.totalJobSeekers || 0) - 1 }
        }));
        setError('');
        return { message: data.message };
      } else {
        setError(data.message);
        throw new Error(data.message);
      }
    } catch (error) {
      setError('Server error while deleting jobseeker');
      console.error('Delete jobseeker error:', error);
      throw error;
    }
  };

  const handleAssignJob = async (formData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/assign-job/${formData.jobSeekerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          jobTitle: formData.jobTitle,
          status: formData.status,
          reason: formData.status === 'rejected' ? formData.reason : undefined
        })
      });
      const data = await response.json();
      if (response.ok) {
        setDashboardData(prev => ({
          ...prev,
          jobSeekers: prev.jobSeekers.map(js =>
            js._id === formData.jobSeekerId
              ? {
                  ...js,
                  jobAssignments: [...(js.jobAssignments || []), data.jobAssignment],
                  totalJobs: (js.totalJobs || 0) + 1
                }
              : js
          ),
          stats: { ...prev.stats, totalJobsAssigned: (prev.stats.totalJobsAssigned || 0) + 1 }
        }));
        setError('');
        return { message: data.message };
      } else {
        setError(data.message);
        throw new Error(data.message);
      }
    } catch (error) {
      setError('Server error while assigning job');
      console.error('Assign job error:', error);
      throw error;
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} setView={setView} />
      <div className="flex-1 md:ml-64">
        <div className="flex items-center justify-between p-4 bg-gray-700 text-white">
          <button className="md:hidden text-white" onClick={() => setIsSidebarOpen(true)}>
            ☰
          </button>
          <h1 className="text-xl font-bold">{user?.userType === 'superadmin' ? 'SuperAdmin Panel' : 'Admin Dashboard'}</h1>
          <Link to="/jadminlogin">
            <button
              className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors"
              onClick={() => localStorage.removeItem('token')}
            >
              Logout
            </button>
          </Link>
        </div>
        <div className="p-6">
          {error && <p className="mb-4 text-red-600">{error}</p>}
          {view === 'dashboard' && <Dashboard user={user} stats={dashboardData.stats} jobSeekers={dashboardData.jobSeekers} />}
          {view === 'jobseekers' && (
            <JobSeekers
              jobSeekers={dashboardData.jobSeekers}
              handleCreateJobSeeker={handleCreateJobSeeker}
              handleUpdateJobSeeker={handleUpdateJobSeeker}
              handleDeleteJobSeeker={handleDeleteJobSeeker}
            />
          )}
          {view === 'jobAssignments' && (
            <JobAssignments
              jobSeekers={dashboardData.jobSeekers}
              handleAssignJob={handleAssignJob}
            />
          )}
          {view === 'jobs' && <Jobs />}
          {view === 'certifyJobSeekers' && <CertifyJobSeekers />}
          {view === 'attendance' && <Attendance />}
          {view === 'searchHistory' && (
            <SearchHistory searchHistory={searchHistory} onSearchJobSeeker={fetchSearchHistory} />
          )}
        </div>
      </div>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default JuniorAdminDashboard;